import settings from '@src/shared/config';

import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import WebSocketClient from './WebSocketClient';

type WebSocketTicketResponse = {
  ticket: string;
  expires_in: number;
};

const WebSocketContext = createContext<WebSocketClient | null>(null);

export function useWebSocketClient() {
  return useContext(WebSocketContext);
}

function buildUrl(path: string, ticket: string) {
  const url = new URL(import.meta.env.VITE_API_DOMAIN);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = path;
  url.search = '';
  url.searchParams.set('ticket', ticket);
  return url.toString();
}

type WebSocketProviderProps = {
  userId?: number;
  path?: string;
  children: ReactNode;
};

/**
 * Owns the single application socket for the signed-in session.
 *
 * This is the only place that knows about ticket authentication;
 * `WebSocketClient` itself just calls `resolveUrl`, so a different scheme
 * would be a change here and nowhere else. A fresh ticket is fetched per
 * attempt because tickets are single-use and expire in 30 seconds.
 */
export default function WebSocketProvider({
  userId,
  path = '/ws/v1/stream/',
  children,
}: WebSocketProviderProps) {
  const { request } = useAPIClient();
  const requestRef = useRef(request);
  requestRef.current = request;

  const client = useMemo(() => {
    if (userId === undefined) return null;
    return new WebSocketClient({
      resolveUrl: async () => {
        const ticket = (await requestRef.current({
          url: 'auth/websocket-ticket/',
          method: 'POST',
        })) as WebSocketTicketResponse;
        return buildUrl(path, ticket.ticket);
      },
      onDebug: settings.DEBUG
        ? (message, detail) => {
            console.info(`[ws] ${message}`, detail);
          }
        : undefined,
    });
    // A new client per signed-in user; `request` is intentionally read through
    // a ref so a new identity does not tear the socket down.
  }, [path, userId]);

  useEffect(() => {
    if (!client) return;

    client.start();
    const reconnect = () => client.reconnectIfClosed();
    const reconnectWhenVisible = () => {
      if (document.visibilityState === 'visible') reconnect();
    };

    window.addEventListener('online', reconnect);
    document.addEventListener('visibilitychange', reconnectWhenVisible);
    return () => {
      window.removeEventListener('online', reconnect);
      document.removeEventListener('visibilitychange', reconnectWhenVisible);
      client.stop();
    };
  }, [client]);

  return (
    <WebSocketContext.Provider value={client}>
      {children}
    </WebSocketContext.Provider>
  );
}
